import { useQuery } from "@tanstack/react-query";
import { LeafletMap, type MapDepot, type MapPin } from "@/components/maps/LeafletMap";
import { listDepots } from "@/services/depots";
import { listMappableLeads } from "@/services/routes";

export function RoutesMap() {
  // Shared cache key with the planner — see Routes/Plan.tsx.
  const leadsQuery = useQuery({ queryKey: ["leads", "mappable"], queryFn: () => listMappableLeads() });
  const depotsQuery = useQuery({ queryKey: ["depots"], queryFn: listDepots });

  const leads: MapPin[] = (leadsQuery.data ?? []).map((l) => ({
    id: l.id,
    lat: l.lat,
    lng: l.lng,
    title: (l.full_name ?? `${l.first_name ?? ""} ${l.last_name ?? ""}`.trim()) || "Lead",
    subtitle: [l.address_street, l.address_city, l.address_state, l.address_zip].filter(Boolean).join(", "),
  }));
  const depots: MapDepot[] = (depotsQuery.data ?? [])
    .filter((d) => d.lat != null && d.lng != null)
    .map((d) => ({
      id: d.id,
      lat: d.lat as number,
      lng: d.lng as number,
      label: d.name,
      title: d.name,
      subtitle: [d.address_street, d.address_city, d.address_state, d.address_zip].filter(Boolean).join(", "),
    }));

  if (leadsQuery.isLoading || depotsQuery.isLoading) {
    return <p className="text-sm text-slate-500">Loading map…</p>;
  }
  if (leadsQuery.error || depotsQuery.error) {
    return <p className="text-sm text-rose-700">Failed to load map data.</p>;
  }
  if (leads.length === 0 && depots.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white p-12 text-center">
        <p className="text-sm font-medium text-slate-700">No mappable leads or depots yet</p>
        <p className="mt-1 text-xs text-slate-500">Import leads, geocode them, and add a depot to see them here.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center gap-4 text-xs text-slate-600">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-slate-400 ring-1 ring-slate-600" />
          {leads.length.toLocaleString()} leads
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-emerald-600" />
          {depots.length} depot{depots.length === 1 ? "" : "s"}
        </span>
      </div>
      <LeafletMap leads={leads} depots={depots} height={620} />
    </div>
  );
}
